import { request } from '@/shared/api/http'
import type {
  AdminAccountCreate,
  AdminAccountPageResponse,
  AdminAccountResponse,
  AdminAccountUpdate,
  AdminChartResponse,
  AdminOverviewResponse,
  ApiKeyIssuedResponse,
  ApiKeyResponse,
  AuditPageResponse,
  FiatCurrency,
  MessageResponse,
  OnchainTxPageResponse,
  TransactionPageResponse,
  WebhookDeliveryPageResponse,
  WebhookDeliveryResponse,
  WebhookEndpointIssuedResponse,
  WebhookEndpointResponse,
  WithdrawalAdminPageResponse,
  WithdrawalAdminResponse,
} from '@/shared/api/types'

/** Админка: аккаунты, сводка по комиссии, очередь выводов, разбор платежей. */
export const admin = {
  // `currency_view` и `account_id` бекенд принимает и здесь: без них
  // админская сводка навсегда оставалась в долларах и по всем сразу,
  // а переключатель валюты в шапке на неё не влиял вовсе.
  overview(view: FiatCurrency = 'usd', accountId?: string) {
    return request<AdminOverviewResponse>('/admin/stats/overview', {
      query: { currency_view: view, ...(accountId ? { account_id: accountId } : {}) },
    })
  },
  commissionChart(days?: number, view: FiatCurrency = 'usd', accountId?: string) {
    return request<AdminChartResponse>('/admin/stats/income-chart', {
      query: { days, currency_view: view, ...(accountId ? { account_id: accountId } : {}) },
    })
  },

  accounts(query: {
    limit?: number; offset?: number; search?: string; currency_view?: FiatCurrency
  } = {}) {
    return request<AdminAccountPageResponse>('/admin/accounts', { query })
  },
  account(id: string) {
    return request<AdminAccountResponse>(`/admin/accounts/${encodeURIComponent(id)}`)
  },
  createAccount(body: AdminAccountCreate) {
    return request<AdminAccountResponse>('/admin/accounts', { method: 'POST', body })
  },
  updateAccount(id: string, body: AdminAccountUpdate) {
    return request<AdminAccountResponse>(`/admin/accounts/${encodeURIComponent(id)}`, {
      method: 'PATCH', body,
    })
  },
  deleteAccount(id: string) {
    return request<MessageResponse>(`/admin/accounts/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    })
  },
  /** Сброс второго фактора: мерчант потерял телефон и резервные коды. */
  resetTwoFactor(id: string) {
    return request<MessageResponse>(`/admin/accounts/${encodeURIComponent(id)}/2fa`, {
      method: 'DELETE',
    })
  },
  accountTransactions(id: string, query: { limit?: number; offset?: number } = {}) {
    return request<TransactionPageResponse>(
      `/admin/accounts/${encodeURIComponent(id)}/transactions`, { query },
    )
  },

  // ---- выводы, ждущие человека ----
  //
  // Заявка выше порога не уходит в сеть, пока её не одобрят руками.
  // Это последний рубеж против угнанной сессии мерчанта.
  pendingWithdrawals(query: { limit?: number; offset?: number } = {}) {
    return request<WithdrawalAdminPageResponse>('/admin/withdrawals/pending', { query })
  },
  approveWithdrawal(id: string) {
    return request<WithdrawalAdminResponse>(
      `/admin/withdrawals/${encodeURIComponent(id)}/approve`, { method: 'POST' },
    )
  },
  rejectWithdrawal(id: string, reason: string) {
    return request<WithdrawalAdminResponse>(
      `/admin/withdrawals/${encodeURIComponent(id)}/reject`,
      { method: 'POST', body: { reason } },
    )
  },

  // ---- разбор поступлений ----
  unmatchedDeposits(query: { limit?: number; offset?: number } = {}) {
    return request<OnchainTxPageResponse>('/admin/onchain/unmatched', { query })
  },
  audit(query: { limit?: number; offset?: number } = {}) {
    return request<AuditPageResponse>('/admin/audit', { query })
  },

  /**
   * Признать поступление потерянным — бухгалтерское сторно.
   *
   * `txid` бекенд требует ЦЕЛИКОМ и вручную, и это не формальность:
   * защита не от подмены (токен админа у того, кто сюда дошёл, и так
   * есть), а от промаха по строке в списке.
   */
  // ---- интеграция: ключи API и вебхуки ----
  //
  // Ключ показывается один раз — в ответе на выдачу. Дальше в списке
  // только префикс. Секрет вебхука — так же.
  apiKeys(accountId: string) {
    return request<ApiKeyResponse[]>(`/admin/accounts/${encodeURIComponent(accountId)}/api-keys`)
  },
  issueApiKey(accountId: string, name: string) {
    return request<ApiKeyIssuedResponse>(
      `/admin/accounts/${encodeURIComponent(accountId)}/api-keys`,
      { method: 'POST', body: { name } },
    )
  },
  revokeApiKey(accountId: string, keyId: string) {
    return request<MessageResponse>(
      `/admin/accounts/${encodeURIComponent(accountId)}/api-keys/${encodeURIComponent(keyId)}`,
      { method: 'DELETE' },
    )
  },
  webhook(accountId: string) {
    return request<WebhookEndpointResponse>(`/admin/accounts/${encodeURIComponent(accountId)}/webhook`)
  },
  setWebhook(accountId: string, url: string) {
    return request<WebhookEndpointIssuedResponse>(
      `/admin/accounts/${encodeURIComponent(accountId)}/webhook`,
      { method: 'PUT', body: { url } },
    )
  },
  clearWebhook(accountId: string) {
    return request<MessageResponse>(
      `/admin/accounts/${encodeURIComponent(accountId)}/webhook`, { method: 'DELETE' },
    )
  },
  webhookDeliveries(accountId: string, query: { limit?: number; offset?: number } = {}) {
    return request<WebhookDeliveryPageResponse>(
      `/admin/accounts/${encodeURIComponent(accountId)}/webhook/deliveries`, { query },
    )
  },
  retryWebhookDelivery(deliveryId: string) {
    return request<WebhookDeliveryResponse>(
      `/admin/webhook-deliveries/${encodeURIComponent(deliveryId)}/retry`, { method: 'POST' },
    )
  },

  orphanDeposit(onchainTxId: string, txid: string, reason: string) {
    return request<MessageResponse>(
      `/admin/onchain/${encodeURIComponent(onchainTxId)}/orphan`,
      { method: 'POST', body: { txid, reason } },
    )
  },

  /**
   * Снять закрепление с застрявшего вывода. Самая опасная ручка шлюза:
   * после неё те же байты могут уйти в сеть повторно.
   */
  releasePin(withdrawalId: string, txid: string, reason: string) {
    return request<WithdrawalAdminResponse>(
      `/admin/withdrawals/${encodeURIComponent(withdrawalId)}/release-pin`,
      { method: 'POST', body: { txid, reason } },
    )
  },
}
